import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import { Menu, Grid, Badge } from "antd";
import IntlMessage from "../util-components/IntlMessage";
import Icon from "../util-components/Icon";
import navigationConfig from "configs/NavigationConfig";
import { connect } from "react-redux";
import { SIDE_NAV_LIGHT, NAV_TYPE_SIDE } from "constants/ThemeConstant";
import utils from "utils";
import { onMobileNavToggle } from "redux/actions/Theme";
import Logo from "./Logo";
import useFinanceiroAtivo from "hooks/useFinanceiroAtivo";
import useIsAppWebView from "hooks/useIsAppWebView";

const { SubMenu } = Menu;
const { useBreakpoint } = Grid;

const setLocale = (isLocaleOn, localeKey) =>
	isLocaleOn ? <IntlMessage id={localeKey} /> : localeKey.toString();

const setDefaultOpen = (key) => {
	let keyList = [];
	let keyString = "";
	if (key) {
		const arr = key.split("-");
		for (let index = 0; index < arr.length; index++) {
			const elm = arr[index];
			index === 0
				? (keyString = elm)
				: (keyString = `${keyString}-${elm}`);
			keyList.push(keyString);
		}
	}
	return keyList;
};

const SideNavContent = (props) => {
	const {
		sideNavTheme,
		routeInfo,
		hideGroupTitle,
		localization,
		onMobileNavToggle,
		notificacoesMenu,
		listaPermissoes,
	} = props;
	const isMobile = !utils.getBreakPoint(useBreakpoint()).includes("lg");
	const financeiroAtivo = useFinanceiroAtivo();
	const isAppWebView = useIsAppWebView();
	const closeMobileNav = () => {
		if (isMobile) {
			onMobileNavToggle(false);
		}
	};

	/*
    Verificar se tem a permissão ou lista de permissões passadas
    por parâmetro
  */
	const verificarPermissoes = useCallback(
		(permissoes) => {
			if (permissoes === undefined || permissoes === null) return true;

			if (!Array.isArray(permissoes)) permissoes = [permissoes];

			// Fica true caso tenha todas permissões
			for (const p of permissoes) {
				const temPermissao = !p || listaPermissoes[p];
				if (temPermissao) {
					return true;
				}
			}

			return false;
		},
		[listaPermissoes],
	);

	/*
    Verificar se o item atende aos requisitos de configuração
    (ex: financeiro ativo)
  */
	const verificarRequisitos = useCallback(
		(item) => {
			// Se requer financeiro ativo, verificar se está habilitado.
			// Comunidade e ranking não passam mais por aqui: os itens ficam
			// sempre visíveis e as próprias telas viram onboarding de
			// ativação quando o módulo está desligado.
			if (item.requireFinanceiroAtivo && !financeiroAtivo) {
				return false;
			}

			return true;
		},
		[financeiroAtivo],
	);

	/*
    Verificar se o item deve ser exibido
    (permissões + requisitos)
  */
	const deveExibirItem = useCallback(
		(item) => {
			// Quando dentro do app (treinoio-profissional WebView), o chat é
			// nativo e o item do menu da web fica oculto pra evitar duplicação.
			// Aulas também fica oculto: conteúdo travado por plano dentro do
			// app viola a política de In-App Purchase da Apple (3.1.1).
			if (isAppWebView && (item.key === "chat" || item.key === "aulas")) {
				return false;
			}
			return verificarPermissoes(item.permissao) && verificarRequisitos(item);
		},
		[verificarPermissoes, verificarRequisitos, isAppWebView],
	);

	/*
    Verifica se esse submenu tem pelo menos um filho que deve ser exibido.
    Se não tiver, retorna false, para que o submenu em si não apareça sem filhos
  */
	const verificarPermissoesSubmenu = useCallback(
		(listaItens) => {
			for (const item of listaItens) {
				let deveExibir = deveExibirItem(item);

				if (item.submenu && item.submenu?.length > 0) {
					deveExibir =
						deveExibir && verificarPermissoesSubmenu(item.submenu);
				}

				if (deveExibir) {
					return true;
				}
			}

			return false;
		},
		[deveExibirItem],
	);

	return (
		<Menu
			theme={sideNavTheme === SIDE_NAV_LIGHT ? "light" : "dark"}
			mode="inline"
			style={{
				height: "100%",
				borderRight: 0,
				overflowY: "auto",
				overflowX: "hidden",
				scrollbarWidth: null,
			}}
			defaultSelectedKeys={[routeInfo?.key]}
			defaultOpenKeys={setDefaultOpen(routeInfo?.key)}
			className={
				(hideGroupTitle ? "hide-group-title" : "") + " hide-scrollbar"
			}
		>
			<Logo />

			{navigationConfig.map((menu) =>
				menu.submenu.length > 0 ? (
					<Menu.ItemGroup
						key={menu.key}
						title={
							menu.showTitle
								? setLocale(localization, menu.title)
								: null
						}
						style={{
							display:
								deveExibirItem(menu) &&
								verificarPermissoesSubmenu(menu.submenu)
									? "block"
									: "none",
						}}
					>
						{menu.submenu.map((subMenuFirst) =>
							subMenuFirst.submenu.length > 0 ? (
								<SubMenu
									key={subMenuFirst.key}
									title={
										<>
											{subMenuFirst.icon ? (
												<Icon
													type={subMenuFirst?.icon}
												/>
											) : null}
											<Badge
												count={
													notificacoesMenu[
														subMenuFirst
															.indexNotificacoes
													]
												}
												offset={[18]}
											>
												{setLocale(
													localization,
													subMenuFirst.title,
												)}
											</Badge>
										</>
									}
									style={{
										display:
											deveExibirItem(subMenuFirst) &&
											verificarPermissoesSubmenu(
												subMenuFirst.submenu,
											)
												? "block"
												: "none",
									}}
								>
									{subMenuFirst.submenu.map(
										(subMenuSecond) => (
											<Menu.Item
												key={subMenuSecond.key}
												style={{
													display: deveExibirItem(
														subMenuSecond,
													)
														? "block"
														: "none",
												}}
											>
												{subMenuSecond.icon ? (
													<Icon
														type={
															subMenuSecond?.icon
														}
													/>
												) : null}
												<Badge
													count={
														notificacoesMenu[
															subMenuSecond
																.indexNotificacoes
														]
													}
													offset={[18]}
												>
													<span>
														{setLocale(
															localization,
															subMenuSecond.title,
														)}
													</span>
												</Badge>
												<Link
													onClick={() =>
														closeMobileNav()
													}
													to={subMenuSecond.path}
												/>
											</Menu.Item>
										),
									)}
								</SubMenu>
							) : (
								<Menu.Item
									key={subMenuFirst.key}
									style={{
										display: deveExibirItem(subMenuFirst)
											? "block"
											: "none",
									}}
								>
									{subMenuFirst.icon ? (
										<Icon type={subMenuFirst.icon} />
									) : null}
									<span>
										{setLocale(
											localization,
											subMenuFirst.title,
										)}
									</span>
									<Link
										onClick={() => closeMobileNav()}
										to={subMenuFirst.path}
									/>
								</Menu.Item>
							),
						)}
					</Menu.ItemGroup>
				) : (
					<Menu.Item
						key={menu.key}
						style={{
							display: deveExibirItem(menu) ? "block" : "none",
						}}
					>
						{menu.icon ? <Icon type={menu?.icon} /> : null}
						<span>
							<Badge
								style={{ marginTop: 5 }}
								count={notificacoesMenu[menu.indexNotificacoes]}
								offset={[18]}
							>
								{setLocale(localization, menu?.title)}
							</Badge>
						</span>
						{menu.path ? (
							<Link
								onClick={() => closeMobileNav()}
								to={menu.path}
							/>
						) : null}
					</Menu.Item>
				),
			)}
		</Menu>
	);
};

const TopNavContent = (props) => {
	const { topNavColor, localization } = props;
	return (
		<Menu mode="horizontal" style={{ backgroundColor: topNavColor }}>
			{navigationConfig.map((menu) =>
				menu.submenu.length > 0 ? (
					<SubMenu
						key={menu.key}
						popupClassName="top-nav-menu"
						title={
							<span>
								{menu.icon ? <Icon type={menu?.icon} /> : null}
								<span>
									{setLocale(localization, menu.title)}
								</span>
							</span>
						}
					>
						{menu.submenu.map((subMenuFirst) =>
							subMenuFirst.submenu.length > 0 ? (
								<SubMenu
									key={subMenuFirst.key}
									icon={
										subMenuFirst.icon ? (
											<Icon type={subMenuFirst?.icon} />
										) : null
									}
									title={setLocale(
										localization,
										subMenuFirst.title,
									)}
								>
									{subMenuFirst.submenu.map(
										(subMenuSecond) => (
											<Menu.Item key={subMenuSecond.key}>
												<span>
													{setLocale(
														localization,
														subMenuSecond.title,
													)}
												</span>
												<Link to={subMenuSecond.path} />
											</Menu.Item>
										),
									)}
								</SubMenu>
							) : (
								<Menu.Item key={subMenuFirst.key}>
									{subMenuFirst.icon ? (
										<Icon type={subMenuFirst?.icon} />
									) : null}
									<span>
										{setLocale(
											localization,
											subMenuFirst.title,
										)}
									</span>
									<Link to={subMenuFirst.path} />
								</Menu.Item>
							),
						)}
					</SubMenu>
				) : (
					<Menu.Item key={menu.key}>
						{menu.icon ? <Icon type={menu?.icon} /> : null}
						<span>{setLocale(localization, menu?.title)}</span>
						{menu.path ? <Link to={menu.path} /> : null}
					</Menu.Item>
				),
			)}
		</Menu>
	);
};

const MenuContent = (props) => {
	return props.type === NAV_TYPE_SIDE ? (
		<SideNavContent {...props} />
	) : (
		<TopNavContent {...props} />
	);
};

const mapStateToProps = ({ theme, menu, permissoes }) => {
	const { sideNavTheme, topNavColor } = theme;
	return {
		sideNavTheme,
		topNavColor,
		notificacoesMenu: menu,
		listaPermissoes: permissoes,
	};
};

export default connect(mapStateToProps, { onMobileNavToggle })(MenuContent);
